import { action, observable, computed, makeObservable } from 'mobx';
import { RemoveAssignedUserStore } from 'nomst/Stores/Configuration/removeAssignedUser.store';
import { AssignUserViewStore } from './assignUserView.store';
import { AssignedUserLevel } from 'typings/enums';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { WorkflowTabs } from 'typings/enums';
import { configurationsStore } from 'utils/MobxDataManager/singletons';
import { navThroughWorkflowView, removeHrReviewer } from 'nomst/Actions/configurations';
import { IConfigurationDataManager } from 'typings/dataManagers';

export class WorkflowSettingsViewStore {
    @observable _tab: WorkflowTabs; // HR Roles
    @observable _assignUserHrApprover: { [id: number]: AssignUserViewStore };
    @observable _assignUserHrAssistants: { [id: number]: AssignUserViewStore[] };
    @observable _removeAssignedUserStore: RemoveAssignedUserStore;
    _configurationDataManager: IConfigurationDataManager;

    constructor() {
        makeObservable(this);
        this._tab = WorkflowTabs.HrRoles;
        this.clearRoles();
        this._configurationDataManager = dataManagerFactory.getConfigurationDataManager();
    }

    get currentCycleId(): number {
        return configurationsStore.cycleSettingsId;
    }

    @computed
    get tab(): WorkflowTabs {
        return this._tab;
    }

    @computed
    get assignUserHrApprover(): AssignUserViewStore {
        if (!this._assignUserHrApprover[this.currentCycleId]) {
            return new AssignUserViewStore('', '', '',
                configurationsStore.masterAccountId,
                this.handleSaveHrApproverUserCallback.bind(this),
                this.handleRemoveHrApproverUserCallback.bind(this));
        }
        return this._assignUserHrApprover[this.currentCycleId].currentCycleId === this.currentCycleId ?
            this._assignUserHrApprover[this.currentCycleId] :
            this._assignUserHrApprover[this.currentCycleId] = new AssignUserViewStore('', '', '',
                configurationsStore.masterAccountId,
                this.handleSaveHrApproverUserCallback.bind(this),
                this.handleRemoveHrApproverUserCallback.bind(this));
    }

    @computed
    get assignUserHrAssistants(): AssignUserViewStore[] {
        if (!this._assignUserHrAssistants[this.currentCycleId]) {
            return [];
        }
        return this._assignUserHrAssistants[this.currentCycleId].slice();
    }

    @computed
    get removeAssignedUserStore(): RemoveAssignedUserStore {
        return this._removeAssignedUserStore;
    }

    async handleSaveHrApproverUserCallback(userId: string): Promise<void> {
        await this._configurationDataManager.saveHrApproverUser(
            configurationsStore.masterAccountId,
            this.currentCycleId,
            userId);
    }

    async handleRemoveHrApproverUserCallback(): Promise<void> {
        await this._configurationDataManager.removeHrApproverUser(
            configurationsStore.masterAccountId,
            this.currentCycleId);
    }

    @action
    async handleSaveHrAssistantUserCallback(userId: string, previousUserId?: string): Promise<void> {
        if (previousUserId) {
            await this.removeHrAssistantUser(previousUserId);
        }

        await this._configurationDataManager.saveHrAssistantUser(
            configurationsStore.masterAccountId,
            this.currentCycleId,
            userId);
    }

    isUserHrApprover(email: string): boolean {
        return email === this.assignUserHrApprover.workEmail;
    }

    isUserHrAssistant(email: string): boolean {
        return this.assignUserHrAssistants.some(assistant => assistant.workEmail === email);
    }

    clearRoles(): void {
        this._assignUserHrApprover = {};
        this._assignUserHrAssistants = {};
        this._removeAssignedUserStore = new RemoveAssignedUserStore();
    }

    @action
    async initData(): Promise<void> {
        this.initDataByCycleId(this.currentCycleId);
    }

    @action
    async initDataByCycleId(cycleId: number): Promise<void> {
        if (Object.keys(this._assignUserHrApprover).indexOf(cycleId.toString()) < 0) {
            await Promise.all([
                this.getHrApproverUserByCycleId(cycleId),
                this.getHrAssistantUsersByCycleId(cycleId)
            ]);
        }
    }

    @action
    getNonAssignedSuggestedAssistantUsers(suggestedUsers: ISuggestedUser[]): ISuggestedUser[] {
        const assignedUserIdDictionary = new Map<string, string>();
        this._assignUserHrAssistants[this.currentCycleId]
            .forEach(assistant => {
                if (assistant.selectedUserId) {
                    assignedUserIdDictionary.set(assistant.selectedUserId, assistant.selectedUserId);
                }
            });

        return suggestedUsers.filter(user => !assignedUserIdDictionary.has(user.userId));
    }

    @action
    changeDetailTabLocal(tab: WorkflowTabs): void {
        this._tab = tab;
    }

    @action
    changeDetailTabGlobal(tab: WorkflowTabs): void {
        this._tab = tab;
        navThroughWorkflowView(tab);
    }

    @action
    async getHrApproverUser(): Promise<void> {
        this.getHrApproverUserByCycleId(this.currentCycleId);
    }

    @action
    async getHrApproverUserByCycleId(cycleId: number): Promise<void> {
        const approverUser: Nullable<IHrReviewerUser> =
            await this._configurationDataManager.getHrApproverUser(
                configurationsStore.masterAccountId, cycleId);

        if (!approverUser) {
            return;
        } else if (Object.keys(this._assignUserHrApprover).indexOf(cycleId.toString()) < 0) {
            this._assignUserHrApprover[cycleId] = new AssignUserViewStore('', '', '',
                configurationsStore.masterAccountId,
                this.handleSaveHrApproverUserCallback.bind(this),
                this.handleRemoveHrApproverUserCallback.bind(this));
            this._assignUserHrAssistants[cycleId] = [];
        }

        const approver = this._assignUserHrApprover[cycleId];
        approver.setUserId(approverUser.userId);
        approver.setWorkEmail(approverUser.workEmail);
        approver.setDisplayName(approverUser.displayName);
    }

    @action
    async getHrAssistantUsers(): Promise<void> {
        this.getHrAssistantUsersByCycleId(this.currentCycleId);
    }

    @action
    async getHrAssistantUsersByCycleId(cycleId: number): Promise<void> {
        const assistantUsers: Nullable<IHrReviewerUser[]> =
            await this._configurationDataManager.getHrAssistantUsers(
                configurationsStore.masterAccountId, cycleId);

        this._assignUserHrAssistants[cycleId] = [];
        assistantUsers?.forEach(assistantUser => {
            const user = new AssignUserViewStore(
                assistantUser.userId,
                assistantUser.workEmail,
                assistantUser.displayName,
                configurationsStore.masterAccountId,
                this.handleSaveHrAssistantUserCallback.bind(this),
                this.handleRemoveHrAssistantUserCallback.bind(this));

            this._assignUserHrAssistants[cycleId].push(user);
        });
    }

    @action
    async removeHrAssistantUser(userId: string): Promise<void> {
        const removedHrAssistant = this._assignUserHrAssistants[this.currentCycleId].find((hrAssistant) =>
            hrAssistant.selectedUserId === userId);

        if (removedHrAssistant) {
            await removedHrAssistant.remove();
            this._assignUserHrAssistants[this.currentCycleId] = this._assignUserHrAssistants[this.currentCycleId].filter(assitant =>
                assitant.selectedUserId !==  removedHrAssistant.selectedUserId);
        }
    }

    @action
    async removeAssignedUser(): Promise<void> {
        if (this._removeAssignedUserStore._assignedUserLevel === AssignedUserLevel.HrApprover) {
            await this._assignUserHrApprover[this.currentCycleId].remove();
        } else if (this._removeAssignedUserStore._assignedUserLevel === AssignedUserLevel.HrAssistant &&
            this._removeAssignedUserStore._assignedUserId) {
            await this.removeHrAssistantUser(this._removeAssignedUserStore._assignedUserId);
        } else {
            const removeAssignedUserModalViewStore = this._removeAssignedUserStore;
            const levelName = removeAssignedUserModalViewStore._accessGroupDisplayName;
            const id = removeAssignedUserModalViewStore._assignedUserId;
            removeHrReviewer(levelName, id);
        }

        this._removeAssignedUserStore.unsetAssignedUser();
    }

    async handleRemoveHrAssistantUserCallback(userId: string): Promise<void> {
        await this._configurationDataManager.removeHrAssistantUser(
            configurationsStore.masterAccountId,
            this.currentCycleId,
            userId);
    }

    @action
    addHrAssistantRow(store: AssignUserViewStore): void {
        this._assignUserHrAssistants[this.currentCycleId].push(store);
    }

    getAssignUserViewStore(): AssignUserViewStore {
        return new AssignUserViewStore(
            '', '', '', configurationsStore.masterAccountId,
            this.handleSaveHrAssistantUserCallback.bind(this),
            this.handleRemoveHrAssistantUserCallback.bind(this));
    }

    getAssignedHrUserApproverViewStore(): AssignUserViewStore {
        return new AssignUserViewStore(
            '', '', '', configurationsStore.masterAccountId,
            this.handleSaveHrApproverUserCallback.bind(this),
            this.handleRemoveHrApproverUserCallback.bind(this));
    }
}
